import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import { EnumCurrency } from "@/app/services/generated/enums/enumCurrency.generated";

export interface CurrencyDefinition {
  currency: EnumCurrency;
  currencySymbolPrefix: string;
  currencySymbolSuffix: string;
  currencyMinDecimalPlaces: number;
  currencyMaxDecimalPlaces: number;
  currencyQuantisedMaxDecimalPlaces: number;
}

const gbp: CurrencyDefinition = {
  currency: EnumCurrency.gBP,
  currencySymbolPrefix: "£",
  currencySymbolSuffix: "",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const eur: CurrencyDefinition = {
  currency: EnumCurrency.eUR,
  currencySymbolPrefix: "€",
  currencySymbolSuffix: "",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const kes: CurrencyDefinition = {
  currency: EnumCurrency.kES,
  currencySymbolPrefix: "Ksh ",
  currencySymbolSuffix: "",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const usd: CurrencyDefinition = {
  currency: EnumCurrency.uSD,
  currencySymbolPrefix: "$",
  currencySymbolSuffix: "",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const aud: CurrencyDefinition = {
  currency: EnumCurrency.aUD,
  currencySymbolPrefix: "A$",
  currencySymbolSuffix: "",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const nzd: CurrencyDefinition = {
  currency: EnumCurrency.nZD,
  currencySymbolPrefix: "NZ$",
  currencySymbolSuffix: "",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const sek: CurrencyDefinition = {
  currency: EnumCurrency.sEK,
  currencySymbolPrefix: "",
  currencySymbolSuffix: " kr",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const zar: CurrencyDefinition = {
  currency: EnumCurrency.zAR,
  currencySymbolPrefix: "R",
  currencySymbolSuffix: "",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const cop: CurrencyDefinition = {
  currency: EnumCurrency.cOP,
  currencySymbolPrefix: "Col$",
  currencySymbolSuffix: "",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const thb: CurrencyDefinition = {
  currency: EnumCurrency.tHB,
  currencySymbolPrefix: "฿",
  currencySymbolSuffix: "",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const php: CurrencyDefinition = {
  currency: EnumCurrency.pHP,
  currencySymbolPrefix: "₱",
  currencySymbolSuffix: "",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const xcd: CurrencyDefinition = {
  currency: EnumCurrency.xCD,
  currencySymbolPrefix: "EC$",
  currencySymbolSuffix: "",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const dkk: CurrencyDefinition = {
  currency: EnumCurrency.dKK,
  currencySymbolPrefix: "",
  currencySymbolSuffix: " kr",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const cad: CurrencyDefinition = {
  currency: EnumCurrency.cAD,
  currencySymbolPrefix: "C$",
  currencySymbolSuffix: "",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const chf: CurrencyDefinition = {
  currency: EnumCurrency.cHF,
  currencySymbolPrefix: "CHF",
  currencySymbolSuffix: "",
  currencyMinDecimalPlaces: 2,
  currencyMaxDecimalPlaces: 5,
  currencyQuantisedMaxDecimalPlaces: 2,
};

const currencyOptions: CurrencyDefinition[] = [
  gbp,
  kes,
  eur,
  usd,
  aud,
  nzd,
  sek,
  zar,
  cop,
  thb,
  php,
  xcd,
  dkk,
  cad,
  chf,
];

export const systemOptions: CurrencyDefinition[] = [
  gbp,
  kes,
  eur,
  usd,
  aud,
  nzd,
  sek,
  zar,
  cop,
  thb,
  php,
  xcd,
];

export function getCurrencyDefinition(currency: EnumCurrency) {
  return currencyOptions.find((x) => x.currency === currency);
}

export function getCurrencyPrefix(currency: EnumCurrency) {
  const currencySettings = currencyOptions.find((x) => x.currency === currency);

  return !currencySettings ||
    currencySettings.currency.isNone() ||
    currencySettings.currency.value ===
      tenantService.settings.currency.currencyCode
    ? tenantService.settings.currency.symbolPrefix // Show currency symbol
    : currencySettings.currency.value + " "; // Show ISO code if currency is not the tenant's currency
}

export function getCurrencySuffix(currency: EnumCurrency) {
  const currencySettings = currencyOptions.find((x) => x.currency === currency);

  return !currencySettings ||
    currencySettings.currency.isNone() ||
    currencySettings.currency.value ===
      tenantService.settings.currency.currencyCode
    ? tenantService.settings.currency.symbolSuffix // Show currency symbol
    : "";
}

export function formatCurrency(
  currency: EnumCurrency,
  value: number,
  quantise: boolean
) {
  const currencySettings = currencyOptions.find((x) => x.currency === currency);
  const prefix = getCurrencyPrefix(currency);
  const suffix = getCurrencySuffix(currency);

  return currencySettings
    ? `${value < 0 ? "-" : ""}${prefix}${tenantService.formatNumber(
        Math.abs(value),
        currencySettings.currencyMinDecimalPlaces,
        quantise
          ? currencySettings.currencyQuantisedMaxDecimalPlaces
          : currencySettings.currencyMaxDecimalPlaces
      )}${suffix}`.trim()
    : tenantService.formatCurrency(value, quantise);
}

export function roundCurrency(
  currency: EnumCurrency,
  value: number,
  quantise: boolean = false
) {
  const currencySettings = currencyOptions.find((x) => x.currency === currency);
  return currencySettings
    ? tenantService.roundCurrency(
        value,
        quantise,
        quantise
          ? currencySettings.currencyQuantisedMaxDecimalPlaces
          : currencySettings.currencyMaxDecimalPlaces
      )
    : tenantService.roundCurrency(value, quantise);
}

export function formatWithMultiCurrency(
  isMultiCurrency: boolean,
  currency: EnumCurrency,
  value: number,
  conversionFactor: number
) {
  return isMultiCurrency
    ? formatCurrency(currency, value, true) +
        " | " +
        tenantService.formatCurrency(value * conversionFactor, true)
    : tenantService.formatCurrency(value, true);
}
